import React, { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { saveSessionEvent } from "../../../session";
import * as Steps from './steps/index';
import ThankYou from '../Common/ThankYou';
import {
  getBasicInfo,
  setBasicInfo,
  getSessionStorageItem,
  storeConsumerApplicationId,
  SESSION_STORAGE_ACCOUNT_KEY,
  SESSION_STORAGE_CUSTOMIZED_TERM_KEY,
  SESSION_STORAGE_AMERICO_OBJECT_KEY,
  SESSION_STORAGE_COVR_FULLUW_KEY,
  SESSION_STORAGE_ACTIVE_PURL,
  SESSION_STORAGE_UTM_DATA,
  SESSION_STORAGE_ANALYSIS_KEY } from '../../../sessionStorage';
import { HeaderProgressTracker } from "../Common/HeaderStepper/HeaderProgressTracker";
import Age from "../../Helpers/Dates/Age";
import { getTermLengthFromAge } from "../../Helpers/Lists/CoverageLengths/AmericoCoverageLengths";
import { getFaceAmountFromAge } from "../../Helpers/Lists/CoverageAmounts/AmericoCoverageAmounts";
import { calculateBMI } from '../../Helpers/Conversion';
import './LoanProtection.scss';
import { CheckIneligibility } from "./Helpers/CheckIneligibility";
import { QuoteProductID } from "../Common/QuoteProductID";

const covrServicesUrl = window.__ENV__.REACT_APP_COVR_SERVICES_URL;

export default function LoanProtection(props) {

  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => { saveSessionEvent(); }, [location]);

  const { path } = useParams();

  const SESSION_STORAGE_KEY = "AMERICO_QUOTE";

  const sessionStorageFormState = JSON.parse(sessionStorage.getItem(SESSION_STORAGE_KEY));

  const prefill = location && location.search && location.search.toLowerCase().includes('prefill');

  const basicInfo = getBasicInfo();
  const age = basicInfo && Age(basicInfo.birthdate);
  const mctQuoterData = getSessionStorageItem(SESSION_STORAGE_AMERICO_OBJECT_KEY);
  const getActivePurlData = getSessionStorageItem(SESSION_STORAGE_ACTIVE_PURL);
  const isActivePurl = getActivePurlData ? getActivePurlData.active : false; 
  const accountObj = getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY);
  const account = accountObj.account;
  const accountId = account.accountId;
  const analysisInfo = getSessionStorageItem(SESSION_STORAGE_ANALYSIS_KEY);
  const promotedProducts = JSON.parse(account.promotedProducts);
  const ineligibleProducts = (promotedProducts && promotedProducts.filter(p => p.isEnabled && p.productHost.toLowerCase() === "americo")) || [];

  function calculateTotalHeight() {
    const radix = 10;
    const heightArray = basicInfo.height.split(" ")
    const heightFeet = parseInt(heightArray[0], radix);
    let heightInches;
    for (let i = 1; i < heightArray.length; i++) {
      if (heightArray[i] !== "" && heightArray[i] !== "ft") {
        heightInches = parseInt(heightArray[i], radix);
        break;
      }
    };
    return heightFeet * 12 + heightInches;
  }

  if (props.landing && sessionStorageFormState) {
    sessionStorageFormState.currentStep = 1;
  };

  useEffect(() => {
    if (basicInfo.height && basicInfo.weight) {
      const weight = basicInfo.weight.split(" ")[0];
      const totalHeightInches = calculateTotalHeight(basicInfo.height);
      const calculatedBMI = calculateBMI(totalHeightInches, weight);
      basicInfo.bmi = calculatedBMI;
      setBasicInfo(basicInfo);
    }
  }, [basicInfo.height, basicInfo.weight]);

  const loanProtProductSettings = accountObj.accountProducts.find(accPrd => accPrd.productLink === '/landing/loan-protection');
  const newDefaultFormState = {
    currentStep: 1,
    policyNumber: "",
    relatedFinancialInstitution: account.accountName,
    submittingAgentCode: loanProtProductSettings && !(loanProtProductSettings.submittingAgentCode === null || loanProtProductSettings.submittingAgentCode === '') ? loanProtProductSettings.submittingAgentCode : 'CVR99B',
    steps: {
      basicInfo: Steps.BasicInfo.DefaultFormState(prefill, basicInfo, false, mctQuoterData, isActivePurl),
      quoter: Steps.Quoter.DefaultFormState(prefill, basicInfo, mctQuoterData),
      personalInfo: Steps.PersonalInfo.DefaultFormState(prefill, basicInfo),
      gettingToKnowYou: Steps.GettingToKnowYou.DefaultFormState(prefill, basicInfo, analysisInfo),
      identityVerification: Steps.IdentityVerification.DefaultFormState(prefill, basicInfo, analysisInfo),
      beneficiary: Steps.Beneficiary.DefaultFormState(prefill),
      personalHistory: Steps.PersonalHistory.DefaultFormState(prefill),
      medicalHistory: Steps.MedicalHistory.DefaultFormState(prefill, mctQuoterData),
      agreementToUnderwriting: Steps.AgreementToUnderwriting.DefaultFormState(prefill),
      policyPurposeConfirmation: Steps.PolicyPurposeConfirmation.DefaultFormState(prefill),
      financialInformation: Steps.FinancialInformation.DefaultFormState(prefill),
      appointment: Steps.Appointment.DefaultFormState(prefill, basicInfo),
    },
    pages: {
      preQuote: {
        coverageAmountQuote: (basicInfo && basicInfo.faceAmount || getFaceAmountFromAge(age)),
        healthClass: (basicInfo && basicInfo.healthClass || "PP"),
        termQuote: (basicInfo && basicInfo.coverageLength || getTermLengthFromAge(age)),
      },
      quoter: Steps.Quoter.DefaultFormState(basicInfo),
      selectAppointment: Steps.SelectAppointment.DefaultFormState(prefill, basicInfo),
    },
    adminMode: false,
    adminCode: "",
    decisionResponse: null,
    fieldsChangedAfterUnderwriting: [],
    oneClickAccountId: account.oneClickAccountId,
    consumerAccountId: accountId,
    isAmerico3: true,
    sessionId: accountObj.sessionId,
  };

  const initialFormState = sessionStorageFormState || newDefaultFormState;
  const mctFlowPages = getSessionStorageItem(SESSION_STORAGE_COVR_FULLUW_KEY);

  const [formState, setFormState] = useState(initialFormState);
  const [multiTermResult, setMultiTermResult] = useState(true);

  // update the formState when the url path changes the step
  useEffect(
    () => {
      if (!path) {
        return;
      }

      const nextStep = steps.find(s => s.path === path);

      // prevent submitted app alterations
      if (!sessionStorageFormState && formState.currentStep === (steps.length - 2)) {

        // if they are backing or directly going to another step, send them to product root
        if (formState.currentStep !== nextStep.step) {
          navigate('/loan-protection');
        }
      }

      if (formState.currentStep === 7 && formState.currentStep > nextStep.step) {
        navigate('/loan-protection/underwriting-offer');
      }

      if (!nextStep || formState.currentStep === nextStep.step) {
        return;
      }
      const newFormState = { ...formState, currentStep: nextStep.step };
      setFormState(newFormState);
    }, [path]);

  const postConsumerApplication = (newFormState) => {
    if (!newFormState) {newFormState = formState;}
    const newForm = mctQuoterData ? { ...newFormState, logoUrl :mctQuoterData.logoUrl, mctQuoterData : mctQuoterData, mctFlowData : mctFlowPages } : newFormState;
    return fetch(`${covrServicesUrl}/api/ConsumerApplication/Post?accountId=${ accountId}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        'SessionId': accountObj.sessionId,
        'X-Consumer-Application-ID': accountObj.consumerApplicationId,
      },
      body: JSON.stringify(newForm),
    }).then(storeConsumerApplicationId);

  };

  const setEmailPrefill = (basicInfo) => {
    setBasicInfo(basicInfo);
  };
  
  const setQuoteEmail = () => {
    const basicInfo = getBasicInfo();
    onFieldChange("selectAppointment", "email", basicInfo.email);
  };

  const onNext = () => {
    const selectedProduct = "Americo";
    if (formState.pages.quoter.product !== selectedProduct) {
      navigate("/term-insurance/select-appointment");
    } else if (formState.pages.quoter.isSelectOrTalk === 'contactSpecialist') {
      navigate("/select-appointment");
    } else {
      const nextStep = steps.find(s => s.step === formState.currentStep + 1);
      
      if (!nextStep) {
        throw new Error("onNext did not find a next step");
      }
      
      const newFormState = { ...formState, currentStep: nextStep.step };
  
      // save email from basicInfo to personalInfo
      if (formState.currentStep === 1) {
        newFormState.steps.personalInfo.emailAddress = newFormState.steps.basicInfo.email;
        setBasicInfo({ ...getBasicInfo(), isRealBirthdate: true });
      }
  
      // save email from personalInfo to basicInfo
      if (formState.currentStep === 4) {
        newFormState.steps.basicInfo.email = newFormState.steps.personalInfo.emailAddress;
      }

      if (CheckIneligibility(formState)) {
        goToStep('ineligible');
        return;
      }
  
      setFormState(newFormState);
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(newFormState));
      const newForm = mctQuoterData ? { ...newFormState, logoUrl :mctQuoterData.logoUrl, mctQuoterData : mctQuoterData, mctFlowData : mctFlowPages } : newFormState;
      newFormState.UTMs = getSessionStorageItem(SESSION_STORAGE_UTM_DATA) || "";
      
      fetch(`${covrServicesUrl}/api/ConsumerApplication/Post?accountId=${ accountId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          'SessionId': accountObj.sessionId,
          'X-Consumer-Application-ID': accountObj.consumerApplicationId,
        },
        body: JSON.stringify(newForm),
      }).then(storeConsumerApplicationId);
  
      if (nextStep.step === 5) {
        saveSessionEvent(true);
      };
  
      navigate(`/loan-protection/${nextStep.path}`);
      window.scroll(0, 0);
    }
  };

  const setPolicyNumber = (value) => {
    setFormState({ ...formState, policyNumber: value });
  };

  const handlePaymentComplete = (appId, policyEffectiveDate) => {
    setFormState({ ...formState, appIdentifier: appId, policyEffectiveDate: policyEffectiveDate });
  };

  const handleApprovedPremiumUpdate = (oldPremium, newPremium) => {
    setFormState({ ...formState, originalPremium: oldPremium, steps: { ...formState.steps, quoter: { ...formState.steps['quoter'], premium: newPremium } } });
  };

  const onBack = () => {
    if (formState.currentStep <= 1) {
      navigate('/', { replace: true });
    }

    const backStep = steps.find(s => s.step === formState.currentStep - 1);
    if(mctQuoterData !== null && backStep.path === 'quote-results'){
      if(mctQuoterData.backUrl){
        navigate(mctQuoterData.backUrl);
      }
      else
      {navigate('/loan-protection/quote-results');}   
      return; 
    }
    if (!backStep) {
      throw new Error("onBack did not find a previous step");
    }

    const newFormState = { ...formState, currentStep: backStep.step };

    setFormState(newFormState);
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(newFormState));

    navigate(`/loan-protection/${backStep.path}`);
    window.scroll(0, 0);
  };

  const onFieldChange = (step, field, value) => {
    if(step === 'basicInfo' && field === 'tobacco'){
      const form = formState;
      form.steps.medicalHistory.nicotineUse = value;
      setFormState(form);
    }
    setFormState({ ...formState, steps: { ...formState.steps, [step]: { ...formState.steps[step], [field]: value } } });
  };

  const goToStep = (path) => {
    const nextStep = steps.find(s => s.path === path);
    if (!nextStep) {
      throw new Error("goToStep did not find a next step");
    }

    const newFormState = { ...formState, currentStep: nextStep.step };

    setFormState(newFormState);
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(newFormState));

    navigate(`/loan-protection/${nextStep.path}`);
    window.scroll(0, 0);
  };

  const getHeaderText = () => {
    const currentStep = formState.currentStep;
    if (currentStep === 1 || currentStep === 2) {
      return "Americo LoanMatch Protector";
    }
    return "LoanMatch Protector";
  };

  const onNextHandOff = (productId) => {
    if(productId === QuoteProductID.PRUDENTIAL_SIMPLY_TERM_PRODUCT_ID){
      navigate(`/term-insurance/prudential`);
    }
    else{
      navigate(`/term-insurance/lga`);
    }
    window.scroll(0, 0);
  };

  // hook onNextApply
  const steps = [
    {
      step: 1,
      path: 'quote-results',
      progressTitle: 'Get a Quote',
      component: <Steps.CustomizeTermQuoter 
          isAmerico={true}
          productType={"Americo"}
          formState={formState.pages.quoter}
          fullFormState={formState}
          preQuoteDetails={formState.pages.preQuote}
          basicInfo={basicInfo}
          onNextHandOff={onNextHandOff}
          onNext={onNext}
          onBack={onBack}
          onFieldChange={(field, value) => onFieldChange('quoter', field, value)}
          setFormState={(coverageAmount, termLength, healthClass) =>
            setFormState({
              ...formState,
              pages: {
                ...formState.pages,
                quoter: {
                  ...formState.pages.quoter,
                  coverageAmount: coverageAmount,
                  term: termLength,
                  healthClass: healthClass,
                },
              },
            })
          }
          onMultiFieldChange={(fs) => {
            setFormState(fs);
            if (account.enableTraditionalTerm) {
              sessionStorage.setItem(SESSION_STORAGE_CUSTOMIZED_TERM_KEY, JSON.stringify({ currentStep: fs.currentStep, pages: fs.pages }));
            }
          }}
          setEmailPrefill={setEmailPrefill}
          setQuoteEmail={setQuoteEmail}
          multiTermResult={multiTermResult}
        />,
        hideMainHeader: true,
        hideProgressTracker: true,
        showContainer: false,
        fullWidth: true,
    },
    {
      step: 2,
      path: 'getting-started',
      progressTitle: 'Getting Started',
      component: (
        <Steps.GettingStarted
          onBack={onBack}
          onNext={onNext}
        />
      ),
      hideStepsDropdown: true,
      categoryName: 'Getting Started',
    },
    {
      step: 3,
      path: 'insured-info',
      component: (
        <Steps.InsuredInfo
          onFieldChange={(page, field, value) => onFieldChange(page, field, value)}
          onBack={onBack}
          onNext={onNext}
          onMultiFieldChange={(fs) => {
            setFormState(fs);
          }}
          fullFormState={formState}
          postConsumerApplication={postConsumerApplication}
        />
      ),
      categoryNumber: 1,
      categoryName: 'Proposed Insured Information',
    },
    {
      step: 4,
      path: 'personal-history',
      component: (
        <Steps.PersonalMedicalHistory
          fullFormState={formState}
          onFieldChange={(page, field, value) => onFieldChange(page, field, value)}
          onBack={onBack}
          onNext={onNext}
          basicInfo={formState.steps.basicInfo}
          onMultiFieldChange={(fs) => {
            setFormState(fs);
          }}
        />
      ),
      categoryNumber: 2,
      categoryName: 'Personal and Medical History',
    },
    {
      step: 5,
      path: 'beneficiary',
      component: (
        <Steps.Beneficiary
          formState={formState.steps.beneficiary}
          onFieldChange={(field, value) => onFieldChange('beneficiary', field, value)}
          onBack={onBack}
          onNext={onNext}
          maritalStatus={formState.steps.gettingToKnowYou.maritalStatus}
        />
      ),
      categoryNumber: 3,
      categoryName: 'Beneficiary',
    },
    {
      step: 6,
      path: 'confirmation',
      component: (
        <Steps.Confirmation
          fullFormState={formState}
          onFieldChange={(page, field, value) => onFieldChange(page, field, value)}
          basicInfo={formState.steps.basicInfo}
          onBack={onBack}
          onNext={onNext}
          setPolicyNumber={setPolicyNumber}
          onDeclined={() => goToStep('ineligible')}
          onMultiFieldChange={(fs) => {
            setFormState(fs);
          }}
        />
      ),
      categoryNumber: 4,
      categoryName: 'Disclosures and Agreements',
    },
    {
      step: 7,
      path: 'underwriting-offer',
      component: (
        <Steps.UnderWritingOffer
          formState={formState}
          underWritingOnFieldChange={onFieldChange}
          offerOnFieldChange={(field, value) => onFieldChange('quoter', field, value)}
          onBack={onBack}
          onNext={onNext}
          basicInfo={formState.steps.basicInfo}
          onDeclined={() => goToStep('ineligible')}
          handleApprovedPremiumUpdate={(oldItem, newItem) => handleApprovedPremiumUpdate(oldItem, newItem)}
          quoter={formState.steps.quoter}
          onMultiFieldChange={(fs) => {
            setFormState(fs);
          }}
          onDecision={(updatedFormState) => {
            setFormState({ ...updatedFormState });
          }}
          onCoverageChanged={() => {
            const changed = formState.fieldsChangedAfterUnderwriting;
            changed.push('coverage');
            setFormState({ ...formState, fieldsChangedAfterUnderwriting: changed });
          }}
        />
      ),
      categoryNumber: 5,
      categoryName: 'Offer and Payment',
    },
    {
      step: 8,
      path: 'payment-information',
      component: <Steps.FinancialInformation
        formState={formState.steps.financialInformation}
        fullFormState={formState}
        onFieldChange={(field, value) => onFieldChange('financialInformation', field, value)}
        onNext={onNext}
        onBack={onBack}
        handlePaymentComplete={handlePaymentComplete}
      />,
      categoryNumber: 5,
      categoryName: 'Offer and Payment',
    },
    {
      step: 9,
      path: 'sign',
      component: <Steps.Sign
        formState={formState}
        SSNNumber={true}
        onNext={onNext}
        onBack={onBack}
        isAmerico3={true}
        redirectPath={"/loan-protection/EsignReturn"}
      />,
      categoryNumber: 5,
      categoryName: 'Offer and Payment',
    },
    {
      step: 10,
      path: 'status',
      component: <Steps.Status
        formState={formState}
        productType={"Americo"}
        basicInfo={formState.steps.basicInfo} />,
      categoryNumber: 5,
      categoryName: 'Summary',
      lastStep: true,
    },

    // not included in flow
    {
      step: 11,
      path: 'appointment',
      progressTitle: 'Appointment',
      component: <Steps.Appointment
        formState={formState.steps.appointment}
        quoter={formState.steps.quoter}
        basicInfo={formState.steps.basicInfo}
        product={`Loan Protection Life Insurance`}
        onFieldChange={(field, value) => onFieldChange('appointment', field, value)}
        onNext={() => goToStep('thank-you')}
        onBack={() => goToStep('get-a-quote')}
        isAppointmentSchedulingEnabled={loanProtProductSettings ? loanProtProductSettings.enableAppointmentScheduling : true}
        baseURL={'/quote/term-life-insurance/loan-protector/appointment'}
        setMultiField={(firstName, lastName, email) => {
          setFormState({ ...formState, pages: { ...formState.pages, appointment: { ...formState.pages.appointment, firstName: firstName, lastName: lastName, email: email } } });
          sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(formState));
        }}
        fullFormState={formState}
        onMultiFieldChange={(fs) => {setFormState(fs);}}
      />,
      hideProgressTracker: true,
      hideMainHeader: true,
    },
    {
      step: 12,
      path: 'thank-you',
      progressTitle: 'ThankYou',
      component: <ThankYou
        sessionStorageKey={SESSION_STORAGE_KEY}
        name={formState.steps.appointment.appointment}
        date={formState.steps.appointment.specificApptTime}
        basicInfo={formState.steps.basicInfo} />,
      hideProgressTracker: true,
      hideMainHeader: false,
    },
    {
      step: 13,
      path: 'ineligible',
      component: <Steps.Ineligible
        ineligibleProducts={ineligibleProducts}
      />,
      categoryNumber: 5,
      categoryName: 'Ineligibility',
    },
  ];
  
  const calculatePercentage = (totalStep, step, categoryStep, currentCateTotalStep) => {
    const stepPer = ((step - 1) / totalStep * 100);
    const cateCount = currentCateTotalStep === 1 ? 1 : (((categoryStep - 0.5) / currentCateTotalStep) * (100 / totalStep));
    return stepPer + cateCount;
  };
  const currentStepObj = steps.find(s => s.step === formState.currentStep) || steps[0];

  const categories = steps.filter((o) => typeof o.categoryNumber !== 'undefined');
  const totalCategories = Math.max(...categories.map((o) => { return o.categoryNumber; }));
  const totalCategoriesStep = Math.max(...categories.map((o) => { return o.categoryStep; }));
  const percent = calculatePercentage(3, currentStepObj.categoryNumber, currentStepObj.categoryStep, totalCategoriesStep);
  const removePadding = ['/loan-protection/quote-results', '/loan-protection/ineligible', '/loan-protection/underwriting-offer'].includes(location.pathname);
  return (
    <div>
      <Helmet>
        <title>{`Loan Protector - ${currentStepObj?.categoryName || currentStepObj?.progressTitle}`}</title>
      </Helmet>
      <HeaderProgressTracker
        breadCrumb={currentStepObj.categoryName ? currentStepObj.categoryName.toUpperCase() : ''}
        breadCrumbLinks={{ name: 'LOANMATCH PROTECTOR', href: '/loan-protection' }}
        isBreadCrumb={true}
        title={getHeaderText()}
        showContainer={currentStepObj.showContainer}
        hidden={props.landing || currentStepObj.hideMainHeader}
        modernizeContainer={true}
        hideProgressTracker={currentStepObj.hideProgressTracker}
        headerProductsText={true}
        stepName={currentStepObj.progressTitle || currentStepObj.categoryName}
        step={currentStepObj.categoryNumber}
        totalSteps={totalCategories}
        whiteBg={currentStepObj.whiteBackground}
        percent={percent}
        hideStepText={currentStepObj.categoryNumber === undefined}
        fullWidth={currentStepObj.fullWidth}>
        <div className={`americoLoanProtector ${removePadding ? "" : "pt-5"} ${currentStepObj.step > 2 && currentStepObj.step !== 10 ? 'border-class' : ''}`}>
          {currentStepObj.component}
        </div>
      </HeaderProgressTracker>
    </div>
  );
}

LoanProtection.propTypes = {
  landing: PropTypes.bool,
};
